import { Component, computed, inject, input, output, signal } from '@angular/core';
import { I18nService } from '../../../../core';
import { AppModalComponent } from '../../../../shared/ui/modal/app-modal.component';
import { LucideAngularModule } from 'lucide-angular';
import { CfNumberFieldComponent } from '../cf-number-field/cf-number-field.component';
import type { CustomFieldRawValue, CustomFieldViewModel } from '../../models/custom-field.model';
import {
  ITEM_LIST_MAX_ROWS, ITEM_LIST_MAX_DESCRIPTION, ITEM_LIST_MAX_LENGTH,
  readItemList, isItemListRow, itemListRowTotal, itemListTotal,
  formatItemListMoney, formatItemListSummary, parseItemListNumber, type ItemListRow,
} from '../../utils/item-list.util';

interface DraftRow { id: number; description: string; quantity: string; price: string; }

@Component({
  selector: 'app-cf-item-list-field',
  standalone: true,
  imports: [AppModalComponent, LucideAngularModule, CfNumberFieldComponent],
  templateUrl: './cf-item-list-field.component.html',
  styleUrl: './cf-item-list-field.component.css',
})
export class CfItemListFieldComponent {
  private readonly i18n = inject(I18nService);
  readonly t = this.i18n.t.bind(this.i18n);
  readonly locale = this.i18n.locale;
  readonly field = input.required<CustomFieldViewModel>();
  readonly value = input<CustomFieldRawValue>(null);
  readonly showLabel = input(true);
  readonly compact = input(false);
  readonly valueChanged = output<CustomFieldRawValue>();
  readonly maxRows = ITEM_LIST_MAX_ROWS;
  readonly maxDescription = ITEM_LIST_MAX_DESCRIPTION;
  readonly priceField = computed<CustomFieldViewModel>(() => ({
    ...this.field(), type: 'CURRENCY', name: this.t('itemList.unitPrice'),
    placeholder: this.t('itemList.unitPrice'), required: true,
  }));
  readonly open = signal(false);
  readonly draft = signal<DraftRow[]>([]);
  readonly attempted = signal(false);
  private nextId = 0;
  private opener: HTMLElement | null = null;
  readonly persisted = computed(() => readItemList(this.value()));
  readonly items = computed(() => this.persisted()?.items ?? []);
  readonly summary = computed(() => formatItemListSummary(this.value(), this.i18n.locale()));
  readonly draftRows = computed(() => this.draft().map(row => this.parseRow(row)));
  readonly draftTotal = computed(() => itemListTotal(this.draftRows().filter((row): row is ItemListRow => row !== null)));
  readonly invalid = computed(() => this.draftRows().some(row => row === null)
    || (this.field().required && this.draft().length === 0)
    || this.serialized().length > ITEM_LIST_MAX_LENGTH);

  money(cents: number): string { return formatItemListMoney(cents, this.i18n.locale()); }
  quantity(value: number): string { return new Intl.NumberFormat(this.i18n.locale(), { maximumFractionDigits: 3 }).format(value); }
  total(row: ItemListRow): string { return this.money(itemListRowTotal(row)); }
  priceValue(row: DraftRow): number | null {
    const cents = parseItemListNumber(row.price, 2);
    return cents == null ? null : cents / 100;
  }
  onPriceChange(id: number, value: CustomFieldRawValue): void {
    if (this.field().readOnly) return;
    const price = typeof value === 'number' && Number.isFinite(value)
      ? this.inputNumber(value.toFixed(2)) : '';
    this.draft.update(rows => rows.map(row => row.id === id ? { ...row, price } : row));
  }
  rowTotal(index: number): string {
    const row = this.draftRows()[index];
    return row ? this.total(row) : '—';
  }
  showEditor(event?: Event): void {
    event?.stopPropagation();
    // Never replace an unreadable stored value with an empty list.
    if (!this.persisted()) return;
    this.opener = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    this.draft.set(this.items().map(row => ({
      id: ++this.nextId, description: row.description,
      quantity: this.inputNumber(String(row.quantity)),
      price: this.inputNumber((row.unitPriceCents / 100).toFixed(2)),
    })));
    this.attempted.set(false);
    if (!this.draft().length && !this.field().readOnly) this.addRow();
    this.open.set(true);
  }
  close(event?: Event): void {
    event?.stopPropagation();
    this.open.set(false);
    this.opener?.focus();
  }
  onDialogKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); this.close(event); return; }
    if (event.key !== 'Tab') return;
    const dialog = event.currentTarget as HTMLElement;
    const controls = dialog.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])');
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }
  addRow(): void {
    if (this.field().readOnly || this.draft().length >= this.maxRows) return;
    this.draft.update(rows => [...rows, { id: ++this.nextId, description: '', quantity: '1', price: '' }]);
  }
  removeRow(id: number): void {
    if (!this.field().readOnly) this.draft.update(rows => rows.filter(row => row.id !== id));
  }
  updateRow(id: number, key: 'description' | 'quantity' | 'price', event: Event): void {
    if (this.field().readOnly) return;
    const value = (event.target as HTMLInputElement).value;
    this.draft.update(rows => rows.map(row => row.id === id ? { ...row, [key]: value } : row));
  }
  error(row: DraftRow): string | null {
    if (!row.description.trim()) return this.t('itemList.descriptionRequired');
    if (row.description.length > this.maxDescription) return this.t('itemList.descriptionTooLong');
    const quantity = parseItemListNumber(row.quantity, 3);
    if (quantity == null || quantity <= 0 || quantity > 9999999) return this.t('itemList.invalidQuantity');
    const price = parseItemListNumber(row.price, 2);
    if (price == null || price < 0 || price > 99999999) return this.t('itemList.invalidPrice');
    return null;
  }
  apply(): void {
    if (this.field().readOnly) return;
    this.attempted.set(true);
    if (this.invalid()) return;
    this.valueChanged.emit(this.draft().length ? this.serialized() : null);
    this.close();
  }
  private inputNumber(value: string): string { return this.locale() === 'pt-BR' ? value.replace('.', ',') : value; }
  private parseRow(row: DraftRow): ItemListRow | null {
    const quantity = parseItemListNumber(row.quantity, 3);
    const price = parseItemListNumber(row.price, 2);
    const parsed = { description: row.description.trim(), quantity: (quantity ?? 0) / 1000, unitPriceCents: price ?? -1 };
    return this.error(row) || !isItemListRow(parsed) ? null : parsed;
  }
  private serialized(): string {
    return JSON.stringify({ version: 1, items: this.draftRows().filter(row => row !== null) });
  }
}
